// TipJarWidget — public widget on /shadow/perfil/:username.
// Shows USDC tip QR + EIP-681 deeplinks + copy address. Wallet-to-wallet, 0% fees.
import React, { useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Check, Copy, ExternalLink, ShieldAlert } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { tShadow } from "../../i18n/shadow-social";

// Chain id + USDC contract (canonical native USDC on each EVM chain)
const CHAINS = {
  base: {
    id: 8453,
    label: "Base",
    color: "#0052ff",
    usdc: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913", // USDC on Base
    explorer: "https://basescan.org/address/",
  },
  ethereum: {
    id: 1,
    label: "Ethereum",
    color: "#627eea",
    usdc: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", // USDC on Mainnet
    explorer: "https://etherscan.io/address/",
  },
  polygon: {
    id: 137,
    label: "Polygon",
    color: "#8247e5",
    usdc: "0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359", // USDC (native) on Polygon
    explorer: "https://polygonscan.com/address/",
  },
  arbitrum: {
    id: 42161,
    label: "Arbitrum",
    color: "#28a0f0",
    usdc: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831", // USDC on Arbitrum
    explorer: "https://arbiscan.io/address/",
  },
  optimism: {
    id: 10,
    label: "Optimism",
    color: "#ff0420",
    usdc: "0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85", // USDC on Optimism
    explorer: "https://optimistic.etherscan.io/address/",
  },
};

const SUGGESTED = [5, 10, 25, 50, 100];
const USDC_DECIMALS = 6;
const toUSDCUnits = (amount) =>
  String(BigInt(Math.round(amount * 10 ** USDC_DECIMALS)));

// EIP-681 transfer URI for ERC-20:
//   ethereum:<token>@<chainId>/transfer?address=<to>&uint256=<amount>
const buildPayUri = (chain, toAddr, amount) => {
  const c = CHAINS[chain];
  if (!c) return "";
  if (!amount) return `ethereum:${toAddr}@${c.id}`;
  return `ethereum:${c.usdc}@${c.id}/transfer?address=${toAddr}&uint256=${toUSDCUnits(
    amount
  )}`;
};

const shortAddr = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : "");

export const TipJarWidget = ({ address, chain = "base" }) => {
  const { lang } = useLanguage();
  const [amount, setAmount] = useState(null);
  const [copied, setCopied] = useState(false);

  const chainInfo = CHAINS[chain] || CHAINS.base;
  const payUri = useMemo(
    () => buildPayUri(chain, address, amount),
    [chain, address, amount]
  );
  const qrPayload = payUri || address;

  if (!address) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* noop */
    }
  };

  return (
    <section
      data-testid="tip-jar-widget"
      style={{
        background:
          "linear-gradient(135deg, rgba(0,212,255,0.05), rgba(245,166,35,0.04))",
        border: "1px solid rgba(245,166,35,0.22)",
        borderRadius: 16,
        padding: 20,
      }}
    >
      <header className="mb-3">
        <h3 className="text-base font-bold text-white">{tShadow("tipJarTitle", lang)}</h3>
        <p className="mt-1 text-xs text-white/55">{tShadow("tipJarSubtitle", lang)}</p>
      </header>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[180px,1fr]">
        {/* QR */}
        <div className="flex flex-col items-center justify-center rounded-xl bg-white p-3">
          <QRCodeSVG
            value={qrPayload}
            size={150}
            level="M"
            bgColor="#ffffff"
            fgColor="#0a0b16"
            data-testid="tip-jar-qr"
          />
          <span
            className="mt-2 text-[9px] font-bold uppercase tracking-[0.18em]"
            style={{ color: chainInfo.color }}
          >
            {chainInfo.label} · USDC
          </span>
        </div>

        {/* Address + amounts */}
        <div className="flex flex-col gap-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/45 mb-1">
              {tShadow("tipJarAddress", lang)}
            </div>
            <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-black/30 px-3 py-2">
              <code
                data-testid="tip-jar-address"
                className="flex-1 truncate font-mono text-xs text-white"
                title={address}
              >
                {address}
              </code>
              <a
                href={chainInfo.explorer + address}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/45 hover:text-white"
                aria-label="explorer"
                data-testid="tip-jar-explorer"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
              <button
                type="button"
                onClick={copy}
                data-testid="tip-jar-copy"
                className="inline-flex items-center gap-1 rounded-lg border border-amber-400/40 bg-amber-400/10 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-amber-200 hover:bg-amber-400/15"
              >
                {copied ? (
                  <>
                    <Check className="h-3 w-3" /> {tShadow("tipJarCopied", lang)}
                  </>
                ) : (
                  <>
                    <Copy className="h-3 w-3" /> {tShadow("tipJarCopy", lang)}
                  </>
                )}
              </button>
            </div>
            <p className="mt-1 text-[10px] text-white/35">{tShadow("tipJarQrHint", lang)}</p>
          </div>

          {/* Suggested amounts */}
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/45 mb-1.5">
              USDC
            </div>
            <div className="flex flex-wrap gap-1.5">
              {SUGGESTED.map((amt) => (
                <button
                  key={amt}
                  type="button"
                  onClick={() => setAmount(amount === amt ? null : amt)}
                  data-testid={`tip-amount-${amt}`}
                  className={`rounded-full border px-3 py-1 text-xs font-bold tabular-nums transition-all ${
                    amount === amt
                      ? "border-amber-400/60 bg-amber-400/[0.15] text-amber-200"
                      : "border-white/15 bg-white/[0.03] text-white/65 hover:border-white/30 hover:text-white"
                  }`}
                >
                  ${amt}
                </button>
              ))}
            </div>
          </div>

          {/* Open in wallet (EIP-681 deeplink) */}
          <a
            href={payUri}
            data-testid="tip-jar-open-wallet"
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-400 to-amber-500 px-4 py-2.5 text-xs font-black uppercase tracking-[0.18em] text-black transition-all hover:from-amber-300 hover:to-amber-400"
            style={{ boxShadow: "0 4px 18px rgba(245,166,35,0.32)" }}
          >
            <ExternalLink className="h-3.5 w-3.5" />
            {tShadow("tipJarOpen", lang)}
            {amount && <span className="font-mono">· ${amount}</span>}
          </a>

          {/* Disclaimer */}
          <p className="inline-flex items-start gap-1.5 rounded-lg border border-rose-500/15 bg-rose-500/[0.04] px-2.5 py-1.5 text-[10px] leading-relaxed text-rose-200/70">
            <ShieldAlert className="mt-0.5 h-3 w-3 shrink-0" />
            {tShadow("tipJarDisclaimer", lang)}
          </p>
        </div>
      </div>
    </section>
  );
};

export default TipJarWidget;
